import React, { useState } from 'react';
import { FiBriefcase } from 'react-icons/fi';
import { useNavigate } from '@tanstack/react-router';
import { GigCard, type GigItem } from '../../components/gigs/GigCard';
import { ApplyGigModal, type ApplyGigData } from '../../components/gigs/ApplyGigModal';
import { useAuth } from '../../hooks/useAuth';
import { isGigActive } from '../../utils/gigDate';

export interface ProfileEvent {
  id: string | number;
  gig_uuid?: string; // New field from /gigs/
  owner_id?: number; // New field from /gigs/
  owner?: {
    id?: number | string;
    username?: string;
    name?: string;
    full_name?: string;
    profile_image?: string | null;
    prof_pics?: string | string[] | null;
  }; // nested owner object from API
  title?: string;
  event_title?: string;
  description?: string;
  info?: string;
  venue?: string;
  location_name?: string;
  location?: string | { city?: string; state?: string; country?: string } | null;
  start_datetime?: string;
  start?: string;
  end_datetime?: string;
  end?: string;
  budget_type?: string;
  budget_min?: string | null;
  budget_max?: string | null;
  budget_amount?: string | null;
  application_start_datetime?: string | null;
  application_end_datetime?: string | null;
  image?: string | null;
  medias?: Array<{ file_url?: string; image?: string }>;
  tags?: string[];
  posted_at?: string;
  created_at?: string;
}

interface GigsTabProps {
  events: ProfileEvent[];
  isLoading?: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
}

// ─── Normalise ProfileEvent → canonical GigItem shape ────────────────────────

function toGigItem(e: ProfileEvent): GigItem {
  const locationObj = typeof e.location === 'object' && e.location != null ? e.location : null;
  const locationName =
    e.venue ||
    e.location_name ||
    (locationObj ? [locationObj.city, locationObj.state].filter(Boolean).join(', ') : '') ||
    (typeof e.location === 'string' ? e.location : '') ||
    '';

  return {
    id: String(e.gig_uuid || e.id),
    gig_uuid: e.gig_uuid,
    owner_id: e.owner_id ?? (e.owner?.id as number | undefined),
    owner: e.owner,
    title: e.title || e.event_title || 'Untitled',
    description: e.description || e.info || '',
    location_name: locationName,
    location: locationObj,
    budget_type: e.budget_type || 'flat_rate',
    budget_min: e.budget_min ?? null,
    budget_max: e.budget_max ?? null,
    budget_amount: e.budget_amount ?? null,
    start_datetime: e.start_datetime || e.start || '',
    end_datetime: e.end_datetime || e.end || '',
    application_start_datetime: e.application_start_datetime ?? null,
    application_end_datetime: e.application_end_datetime ?? null,
    status: 'active',
    event_type: null,
    image: e.image || e.medias?.[0]?.file_url || e.medias?.[0]?.image || null,
    tags: e.tags || [],
    created_at: e.created_at || e.posted_at,
  };
}

// ─── Tab component ────────────────────────────────────────────────────────────

export const GigsTab: React.FC<GigsTabProps> = ({
  events,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [applyTarget, setApplyTarget] = useState<{ gig: ApplyGigData; gigId: string } | null>(null);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 md:p-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-80 rounded-[18px] bg-zinc-900 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
        <div className="w-16 h-16 rounded-full bg-zinc-900 flex items-center justify-center text-zinc-600">
          <FiBriefcase size={28} />
        </div>
        <div>
          <p className="text-white text-lg font-semibold">No Gigs Yet</p>
          <p className="text-zinc-500 text-sm mt-1">No gigs have been posted yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 md:p-6">
        {events.map((event) => {
          const gigItem = toGigItem(event);
          const gigId = gigItem.id;
          const isOwner =
            !!user?.id && !!gigItem.owner_id && String(user.id) === String(gigItem.owner_id);
          const gigExpired = !isGigActive(gigItem);
          return (
            <GigCard
              key={gigId}
              gig={gigItem}
              onViewDetails={() =>
                navigate({ to: '/gigs/$gigId', params: { gigId }, search: { apply: false } })
              }
              isOwner={isOwner}
              onEdit={
                isOwner && !gigExpired
                  ? () =>
                      navigate({
                        to: '/gigs/$gigId/edit',
                        params: { gigId },
                        search: { apply: false },
                      })
                  : undefined
              }
              onApply={() => setApplyTarget({ gig: gigItem as unknown as ApplyGigData, gigId })}
            />
          );
        })}
      </div>

      {/* Load more footer */}
      {(hasMore || isLoadingMore) && (
        <div className="flex justify-center py-6">
          {isLoadingMore ? (
            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          ) : (
            <button
              onClick={onLoadMore}
              className="px-6 py-2.5 rounded-xl bg-zinc-900 border border-white/10 text-zinc-300 text-[14px] font-medium hover:border-white/20 hover:text-white transition-all"
            >
              Load more
            </button>
          )}
        </div>
      )}

      {!hasMore && events.length > 0 && (
        <p className="text-center text-zinc-700 text-[13px] py-4">
          All {events.length} gig{events.length !== 1 ? 's' : ''} loaded
        </p>
      )}

      {/* Apply to Gig modal */}
      <ApplyGigModal
        isOpen={applyTarget !== null}
        onClose={() => setApplyTarget(null)}
        gig={applyTarget?.gig ?? null}
        gigId={applyTarget?.gigId ?? ''}
        user={
          user
            ? {
                fullName: (user as { full_name?: string }).full_name,
                username: (user as { username?: string }).username,
              }
            : null
        }
      />
    </div>
  );
};
